import React, { useState, useEffect } from "react";
import {
  Card,
  Table,
  Row,
  Col,
  Statistic,
  DatePicker,
  Button,
  Space,
  Tag,
  Dropdown,
  Modal,
  Form,
  Input,
  Select,
  Alert,
  Descriptions,
  Typography,
  Spin,
} from "antd";
import {
  DollarOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  MoreOutlined,
  CheckOutlined,
  EyeOutlined,
  UserOutlined,
  CalendarOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import PageMeta from "~/components/common/meta";
import { BillingSubNav } from "~/components/admin/billing";
import {
  useGetPayrollInvoices,
  useGetPayrollDashboard,
  useMarkPayrollInvoiceAsPaid,
  useCreatePayrollInvoiceWithQuickBooks,
  useGetPayrollInvoice,
} from "~/services/payroll";
import { useGetAllStaff } from "~/services/operation";
import { usePayrollSettings } from "~/hooks/usePayrollSettings";
import { useNotification } from "~/hooks";
import { formatStaffName } from "~/utils/format-staff-name";
import {
  PayrollInvoiceStatus,
  PayrollInvoice,
  PayrollPeriodType,
} from "~/types/payroll";
import {
  getQuickBooksConnectionStatus,
  getQuickBooksAuthUrl,
  QuickBooksConnectionStatus,
} from "~/services/quickbooks/quickbooks.api";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

export const ScreenPayroll: React.FC = () => {
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [form] = Form.useForm();
  const [qbConnectionStatus, setQbConnectionStatus] =
    useState<QuickBooksConnectionStatus | null>(null);

  const { success } = useNotification();

  const { data: payrollInvoices, isLoading: invoicesLoading } =
    useGetPayrollInvoices({
      page: 1,
      limit: 50,
      startDate: dateRange?.[0],
      endDate: dateRange?.[1],
    });

  const { data: dashboardData, isLoading: dashboardLoading } =
    useGetPayrollDashboard(dateRange?.[0], dateRange?.[1]);

  const { data: staffsData, isLoading: staffsLoading } = useGetAllStaff({
    getAll: true,
  });
  const { settings: payrollSettings, isLoading: settingsLoading } =
    usePayrollSettings();

  const markAsPaidMutation = useMarkPayrollInvoiceAsPaid();
  const createWithQuickBooksMutation = useCreatePayrollInvoiceWithQuickBooks();

  const { data: invoiceDetail, isLoading: detailLoading } =
    useGetPayrollInvoice(selectedInvoiceId || "");

  useEffect(() => {
    if (payrollSettings && !form.getFieldValue("period")) {
      const today = dayjs();
      let startDate, endDate;

      switch (payrollSettings.payPeriod) {
        case "weekly":
          startDate = today.startOf("week");
          endDate = today.endOf("week");
          break;
        case "biweekly":
          startDate = today.startOf("week");
          endDate = today.add(1, "week").endOf("week");
          break;
        case "monthly":
          startDate = today.startOf("month");
          endDate = today.endOf("month");
          break;
        default:
          startDate = today.startOf("week");
          endDate = today.endOf("week");
      }

      form.setFieldsValue({
        period: [startDate, endDate],
        periodType:
          payrollSettings.payPeriod === "biweekly"
            ? PayrollPeriodType.BIWEEKLY
            : payrollSettings.payPeriod === "monthly"
            ? PayrollPeriodType.MONTHLY
            : PayrollPeriodType.WEEKLY,
      });
    }
  }, [payrollSettings, form]);

  useEffect(() => {
    checkQuickBooksConnection();
  }, []);

  const checkQuickBooksConnection = async () => {
    try {
      const status = await getQuickBooksConnectionStatus();
      setQbConnectionStatus(status);
    } catch (error: any) {
      console.error("Error checking QuickBooks connection:", error);
      setQbConnectionStatus({
        isConnected: false,
        error: "Failed to check connection status",
        authUrl: "/api/quickbooks/auth/url",
      });
    }
  };

  const handleConnectQuickBooks = async () => {
    try {
      const { authUrl } = await getQuickBooksAuthUrl();
      window.open(authUrl, "_blank");
      setTimeout(() => {
        checkQuickBooksConnection();
      }, 3000);
    } catch (error: any) {
      console.error("Error getting auth URL:", error);
      error({
        message: "❌ Failed to get QuickBooks authorization URL",
        description:
          "Please try again later or contact support for assistance.",
      });
    }
  };

  const getStatusColor = (status: PayrollInvoiceStatus) => {
    switch (status) {
      case PayrollInvoiceStatus.DRAFT:
        return "default";
      case PayrollInvoiceStatus.PENDING:
        return "processing";
      case PayrollInvoiceStatus.APPROVED:
        return "warning";
      case PayrollInvoiceStatus.PAID:
        return "success";
      case PayrollInvoiceStatus.CANCELLED:
        return "error";
      default:
        return "default";
    }
  };

  const handleMarkAsPaid = (id: string) => {
    markAsPaidMutation.mutate(id);
  };

  const handleCreatePayrollInvoice = () => {
    setCreateModalVisible(true);
  };

  const handleCreateSubmit = async (values: any) => {
    try {
      const result = await createWithQuickBooksMutation.mutateAsync({
        staffId: values.staffId,
        periodStart: values.period[0].format("YYYY-MM-DD"),
        periodEnd: values.period[1].format("YYYY-MM-DD"),
        periodType: values.periodType,
        notes: values.notes,
        autoCalculate: true,
      });

      setCreateModalVisible(false);
      form.resetFields();

      const staffName = staffsData?.items?.find(
        (staff: any) => staff.id === values.staffId
      );
      const staffFullName = staffName
        ? `${staffName.user?.firstName} ${staffName.user?.lastName}`
        : "Employee";

      success({
        message: "✅ Payroll Invoice Created Successfully!",
        description: (
          <div>
            <div>
              Employee: <span className="font-medium">{staffFullName}</span>
            </div>
            <div>
              Period: {values.period[0].format("MMM DD")} -{" "}
              {values.period[1].format("MMM DD, YYYY")}
            </div>
            <div>
              QuickBooks: <span className="text-green-600">✓ Synced</span>
            </div>
            {(result as any)?.data?.invoiceNumber && (
              <div>
                Invoice #:{" "}
                <span className="font-medium">
                  {(result as any).data.invoiceNumber}
                </span>
              </div>
            )}
          </div>
        ),
      });
    } catch (error: any) {
      console.error("Error creating payroll invoice:", error);
      error({
        message: "❌ Failed to Create Payroll Invoice",
        description:
          error?.response?.data?.message ||
          error?.message ||
          "An unexpected error occurred",
      });
    }
  };

  const handleCreateCancel = () => {
    setCreateModalVisible(false);
    form.resetFields();
  };

  const handleViewDetail = (invoiceId: string) => {
    setSelectedInvoiceId(invoiceId);
    setDetailModalVisible(true);
  };

  const handleDetailModalClose = () => {
    setDetailModalVisible(false);
    setSelectedInvoiceId(null);
  };

  const columns: ColumnsType<PayrollInvoice> = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
    },
    {
      title: "Employee",
      key: "employee",
      render: (_, record) => (
        <div>
          <div className="font-medium">
            {record.staff?.user?.firstName} {record.staff?.user?.lastName}
          </div>
          <div className="text-gray-500 text-sm">
            {record.staff?.user?.email}
          </div>
        </div>
      ),
    },
    {
      title: "Period",
      key: "period",
      render: (_, record) => (
        <div className="text-sm">
          {new Date(record.periodStart).toLocaleDateString()} -{" "}
          {new Date(record.periodEnd).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: "Regular Hours",
      dataIndex: "regularHours",
      key: "regularHours",
      render: (hours: any) => `${(Number(hours) || 0).toFixed(1)}h`,
    },
    {
      title: "Overtime Hours",
      dataIndex: "overtimeHours",
      key: "overtimeHours",
      render: (hours: any) => `${(Number(hours) || 0).toFixed(1)}h`,
    },
    {
      title: "Gross Pay",
      dataIndex: "grossPay",
      key: "grossPay",
      render: (amount: any) => `$${(Number(amount) || 0).toFixed(2)}`,
    },
    {
      title: "Net Pay",
      dataIndex: "netPay",
      key: "netPay",
      render: (amount: any) => `$${(Number(amount) || 0).toFixed(2)}`,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: PayrollInvoiceStatus) => (
        <Tag color={getStatusColor(status)}>{status.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Dropdown
          menu={{
            items: [
              {
                key: "view",
                label: "View Details",
                icon: <EyeOutlined />,
                onClick: () => handleViewDetail(record.id),
              },
              ...(record.status === PayrollInvoiceStatus.APPROVED
                ? [
                    {
                      key: "paid",
                      label: "Mark as Paid",
                      icon: <DollarOutlined />,
                      onClick: () => handleMarkAsPaid(record.id),
                    },
                  ]
                : []),
            ],
          }}
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageMeta title="Payroll | Art Handler" />
      <BillingSubNav />

      {qbConnectionStatus && !qbConnectionStatus.isConnected && (
        <Alert
          message="QuickBooks Not Connected"
          description={
            <div>
              <p>
                {qbConnectionStatus.error ||
                  "Please connect to QuickBooks to create payroll invoices."}
              </p>
              <Button
                type="primary"
                onClick={handleConnectQuickBooks}
                className="mt-2"
              >
                Connect to QuickBooks
              </Button>
            </div>
          }
          type="warning"
          showIcon
          className="mb-4"
        />
      )}

      {qbConnectionStatus && qbConnectionStatus.isConnected && (
        <Alert
          message="🔗 QuickBooks Connected"
          description={`Connected to ${
            qbConnectionStatus.companyName || "QuickBooks"
          }. Ready to create payroll invoices.`}
          type="success"
          showIcon
          className="mb-4"
        />
      )}

      <Row gutter={16}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Draft"
              value={dashboardData?.totalDraft || 0}
              prefix={<DollarOutlined />}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Pending Approval"
              value={dashboardData?.totalPending || 0}
              prefix={<ClockCircleOutlined />}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Approved"
              value={dashboardData?.totalApproved || 0}
              prefix={<CheckOutlined />}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Total Amount"
              value={dashboardData?.totalAmount || 0}
              prefix="$"
              precision={2}
              loading={dashboardLoading}
            />
          </Card>
        </Col>
      </Row>

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Payroll Management</h1>
        <Space>
          <RangePicker
            onChange={(dates) => {
              if (dates) {
                setDateRange([
                  dates[0]?.format("YYYY-MM-DD") || "",
                  dates[1]?.format("YYYY-MM-DD") || "",
                ]);
              } else {
                setDateRange(null);
              }
            }}
          />
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreatePayrollInvoice}
          >
            Create Payroll Invoice
          </Button>
        </Space>
      </div>

      <Card title="Payroll Invoices" className="shadow-sm">
        <Table
          dataSource={payrollInvoices?.data || []}
          columns={columns}
          loading={invoicesLoading}
          pagination={{
            current: payrollInvoices?.page || 1,
            pageSize: payrollInvoices?.limit || 50,
            total: payrollInvoices?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} invoices`,
          }}
          locale={{
            emptyText:
              "No payroll invoices found. Create your first payroll invoice to get started.",
          }}
        />
      </Card>

      <Modal
        title={
          <div>
            <div className="text-lg font-semibold">Create Payroll Invoice</div>
            <div className="text-sm text-gray-500 mt-1">
              Create a new payroll invoice for an employee. The system will
              automatically calculate hours and pay based on work sessions.
            </div>
          </div>
        }
        open={createModalVisible}
        onCancel={handleCreateCancel}
        footer={null}
        width={600}
      >
        <Form form={form} onFinish={handleCreateSubmit} layout="vertical">
          <Form.Item name="period" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="periodType" hidden>
            <Input />
          </Form.Item>

          <Form.Item
            name="staffId"
            label="Select Employee"
            rules={[{ required: true, message: "Please select an employee" }]}
          >
            <Select
              placeholder="Search and choose an employee"
              loading={staffsLoading}
              showSearch
              filterOption={(input, option) => {
                const optionText = String(option?.label || "");
                return optionText.toLowerCase().includes(input.toLowerCase());
              }}
              optionLabelProp="label"
            >
              {staffsData?.items?.map((staff: any) => (
                <Option
                  key={staff.id}
                  value={staff.id}
                  label={`${staff.user?.firstName} ${staff.user?.lastName} - ${staff.user?.email}`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">
                      {staff.user?.firstName} {staff.user?.lastName}
                    </span>
                    <span className="text-xs text-gray-500">
                      {staff.user?.email}
                    </span>
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="notes" label="Notes (Optional)">
            <TextArea
              rows={3}
              placeholder="Add any additional notes or comments about this payroll invoice..."
            />
          </Form.Item>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <div className="text-sm text-blue-800">
              <strong>Payroll Settings:</strong>
              <div className="mt-2 space-y-1">
                <div>
                  • Pay Period:{" "}
                  <span className="font-medium">
                    {payrollSettings?.payPeriod || "weekly"}
                  </span>
                </div>
                <div>
                  • Period Type:{" "}
                  <span className="font-medium">
                    {payrollSettings?.payPeriod === "biweekly"
                      ? "Bi-weekly"
                      : payrollSettings?.payPeriod === "monthly"
                      ? "Monthly"
                      : "Weekly"}
                  </span>
                </div>
                <div>
                  • Payment Day:{" "}
                  <span className="font-medium">
                    {payrollSettings?.paymentDay || "Friday"}
                  </span>
                </div>
              </div>
            </div>
            <div className="text-sm text-blue-800 mt-3">
              <strong>Note:</strong> When you create this payroll invoice, the
              system will:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>
                  Automatically calculate regular and overtime hours from work
                  sessions
                </li>
                <li>Apply the employee's current pay rates</li>
                <li>Include any bonuses or deductions</li>
                <li>Generate the final gross and net pay amounts</li>
                <li>Create and sync with QuickBooks immediately</li>
              </ul>
            </div>
          </div>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={
                  createWithQuickBooksMutation.isPending || settingsLoading
                }
                icon={<PlusOutlined />}
              >
                Create Payroll Invoice
              </Button>
              <Button onClick={handleCreateCancel}>Cancel</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          <div className="flex items-center space-x-2">
            <FileTextOutlined className="text-blue-500" />
            <span className="text-lg font-semibold">
              Payroll Invoice Details
            </span>
          </div>
        }
        open={detailModalVisible}
        onCancel={handleDetailModalClose}
        footer={[
          <Button key="close" onClick={handleDetailModalClose}>
            Close
          </Button>,
        ]}
        width={800}
        className="top-8"
      >
        <Spin spinning={detailLoading}>
          {invoiceDetail && (
            <div className="space-y-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg">
                <div className="flex justify-between items-start">
                  <div>
                    <Title level={4} className="mb-2">
                      Invoice #{invoiceDetail.invoiceNumber}
                    </Title>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <span>
                        <CalendarOutlined />{" "}
                        {new Date(invoiceDetail.createdAt).toLocaleDateString()}
                      </span>
                      <Tag color={getStatusColor(invoiceDetail.status)}>
                        {invoiceDetail.status.toUpperCase()}
                      </Tag>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">
                      ${(Number(invoiceDetail.netPay) || 0).toFixed(2)}
                    </div>
                    <div className="text-sm text-gray-500">Net Pay</div>
                  </div>
                </div>
              </div>

              <Card
                title={
                  <span>
                    <UserOutlined /> Employee Information
                  </span>
                }
                size="small"
              >
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Name">
                    {invoiceDetail.staff?.user?.firstName}{" "}
                    {invoiceDetail.staff?.user?.lastName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {invoiceDetail.staff?.user?.email}
                  </Descriptions.Item>
                  <Descriptions.Item label="Staff">
                    {formatStaffName(invoiceDetail.staff)}
                  </Descriptions.Item>
                  <Descriptions.Item label="Regular Rate">
                    ${(Number(invoiceDetail.regularRate) || 0).toFixed(2)}/hr
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card
                title={
                  <span>
                    <CalendarOutlined /> Pay Period
                  </span>
                }
                size="small"
              >
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Period Start">
                    {new Date(invoiceDetail.periodStart).toLocaleDateString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period End">
                    {new Date(invoiceDetail.periodEnd).toLocaleDateString()}
                  </Descriptions.Item>
                  <Descriptions.Item label="Period Type">
                    {invoiceDetail.periodType}
                  </Descriptions.Item>
                  <Descriptions.Item label="Total Days">
                    {Math.ceil(
                      (new Date(invoiceDetail.periodEnd).getTime() -
                        new Date(invoiceDetail.periodStart).getTime()) /
                        (1000 * 60 * 60 * 24)
                    )}{" "}
                    days
                  </Descriptions.Item>
                </Descriptions>
              </Card>

              <Card
                title={
                  <span>
                    <ClockCircleOutlined /> Hours & Pay Breakdown
                  </span>
                }
                size="small"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Regular Hours:</span>
                      <span className="font-medium">
                        {(Number(invoiceDetail.regularHours) || 0).toFixed(1)}h
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Overtime Hours:</span>
                      <span className="font-medium">
                        {(Number(invoiceDetail.overtimeHours) || 0).toFixed(1)}h
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Hours:</span>
                      <span className="font-medium">
                        {(
                          (Number(invoiceDetail.regularHours) || 0) +
                          (Number(invoiceDetail.overtimeHours) || 0)
                        ).toFixed(1)}
                        h
                      </span>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Regular Pay:</span>
                      <span className="font-medium">
                        ${(Number(invoiceDetail.regularPay) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Overtime Pay:</span>
                      <span className="font-medium">
                        ${(Number(invoiceDetail.overtimePay) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Gross Pay:</span>
                      <span className="font-medium text-green-600">
                        ${(Number(invoiceDetail.grossPay) || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              </Card>

              {(Number(invoiceDetail.deductions) || 0) > 0 && (
                <Card
                  title={
                    <span>
                      <DollarOutlined /> Deductions
                    </span>
                  }
                  size="small"
                >
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total Deductions:</span>
                      <span className="font-medium text-red-600">
                        -${(Number(invoiceDetail.deductions) || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between text-lg font-bold">
                      <span>Net Pay:</span>
                      <span className="text-green-600">
                        ${(Number(invoiceDetail.netPay) || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </Card>
              )}

              {(() => {
                const qbInvoice = invoiceDetail.metadata?.quickbooksInvoice;
                if (!qbInvoice || typeof qbInvoice !== "object") return null;

                const qbData = qbInvoice as Record<string, unknown>;

                return (
                  <Card
                    title={
                      <span>
                        <FileTextOutlined /> QuickBooks Invoice
                      </span>
                    }
                    size="small"
                  >
                    <Descriptions column={2} size="small">
                      <Descriptions.Item label="QB Invoice ID">
                        {qbData.qbInvoiceId
                          ? String(qbData.qbInvoiceId)
                          : "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="QB Customer ID">
                        {qbData.qbCustomerId
                          ? String(qbData.qbCustomerId)
                          : "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Sync Status">
                        <Tag color="green">Synced</Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Sync Date">
                        {new Date(invoiceDetail.createdAt).toLocaleDateString()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Card>
                );
              })()}

              {invoiceDetail.workSessions &&
                invoiceDetail.workSessions.length > 0 && (
                  <Card
                    title={
                      <span>
                        <ClockCircleOutlined /> Work Sessions (
                        {invoiceDetail.workSessions.length})
                      </span>
                    }
                    size="small"
                  >
                    <div className="space-y-2 max-h-40 overflow-y-auto">
                      {invoiceDetail.workSessions.map(
                        (session: any, _: number) => (
                          <div
                            key={session.id}
                            className="flex justify-between items-center p-2 bg-gray-50 rounded"
                          >
                            <div className="text-sm">
                              <div className="font-medium">
                                {session.serviceTicket?.serviceType ||
                                  "Work Session"}
                              </div>
                              <div className="text-gray-500">
                                {new Date(
                                  session.startTime
                                ).toLocaleDateString()}{" "}
                                -{" "}
                                {new Date(session.endTime).toLocaleDateString()}
                              </div>
                            </div>
                            <div className="text-sm font-medium">
                              {(
                                (new Date(session.endTime).getTime() -
                                  new Date(session.startTime).getTime()) /
                                (1000 * 60 * 60)
                              ).toFixed(1)}
                              h
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  </Card>
                )}

              {invoiceDetail.notes && (
                <Card title={<span>Notes</span>} size="small">
                  <Text className="text-gray-700">{invoiceDetail.notes}</Text>
                </Card>
              )}
            </div>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default ScreenPayroll;
